import React, { useEffect, useMemo } from "react";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  MutateSetActiveTrackMap,
  QueryAvailableConfigurationsForCircuit,
  QueryTrackMapPreview,
} from "../../../utils/graphql/hooks/trackMapHooks";
import {
  setSelectedCircuit,
  setSelectedConfiguration,
} from "../../../state/trackMapReducer";
import { Circuit, Configuration } from "../../../types/trackMap";
import TrackMapPreview from "../TrackMapPreview";
import { toast } from "react-hot-toast";

export default function TrackMapSelector() {
  const dispatch = useDispatch();

  const { queryAvailableConfigurationsForCircuit } =
    QueryAvailableConfigurationsForCircuit();
  const { queryTrackMapPreview } = QueryTrackMapPreview();
  const setActiveTrackMap = MutateSetActiveTrackMap();

  const availableCircuits = useSelector(
    (state: RootState) => state.trackMap?.availableCircuits,
    isEqual,
  );

  const availableConfigurationsForCircuit = useSelector(
    (state: RootState) => state.trackMap?.availableConfigurationsForCircuit,
    isEqual,
  );

  const selectedCircuit = useSelector(
    (state: RootState) => state.trackMap?.selectedCircuit,
    isEqual,
  );

  const selectedConfiguration = useSelector(
    (state: RootState) => state.trackMap?.selectedConfiguration,
    isEqual,
  );

  const trackMapPreview = useSelector(
    (state: RootState) => state.trackMap?.trackMapPreview,
    isEqual,
  );

  const trackMap = useSelector(
    (state: RootState) => state.trackMap?.trackMap,
    isEqual,
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!trackMap || !trackMapPreview) return false;
    return (
      trackMap.circuitID !== trackMapPreview.circuitID ||
      trackMap.configurationID !== trackMapPreview.configurationID
    );
  }, [trackMap, trackMapPreview]);

  useEffect(() => {
    if (selectedCircuit?.globalUniqueId) {
      queryAvailableConfigurationsForCircuit(selectedCircuit.globalUniqueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCircuit?.globalUniqueId]);

  const handleSave = async () => {
    if (selectedCircuit && selectedConfiguration) {
      const response = await setActiveTrackMap({
        variables: {
          circuitName: selectedCircuit.fullName,
          circuitID: selectedCircuit.globalUniqueId,
          configurationName: selectedConfiguration.name,
          configurationID: selectedConfiguration.globalUniqueId,
        },
      });
      if (response.data?.setActiveTrackMap) {
        toast.success("Track map set successfully!");
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>Track Map</div>
      <div className={styles.selectorRow}>
        <select
          className={styles.selector}
          onChange={(e) => {
            const circuit = availableCircuits.find(
              (c: Circuit) => c.globalUniqueId === e.target.value,
            );
            if (circuit) dispatch(setSelectedCircuit(circuit));
          }}
          value={selectedCircuit?.globalUniqueId ?? ""}
        >
          <option value="" disabled hidden>
            Circuit
          </option>
          {availableCircuits?.map((circuit: Circuit) => (
            <option key={circuit.globalUniqueId} value={circuit.globalUniqueId}>
              {circuit.fullName}
            </option>
          ))}
        </select>

        <select
          className={styles.selector}
          onChange={(e) => {
            const config = availableConfigurationsForCircuit.find(
              (c: Configuration) => c.globalUniqueId === e.target.value,
            );
            if (config) {
              dispatch(setSelectedConfiguration(config));
              queryTrackMapPreview({
                circuitName: selectedCircuit?.fullName || "",
                circuitID: selectedCircuit?.globalUniqueId || "",
                configurationName: config.name,
                configurationID: config.globalUniqueId,
              });
            }
          }}
          value={selectedConfiguration?.globalUniqueId ?? ""}
          disabled={
            !selectedCircuit || availableConfigurationsForCircuit.length === 0
          }
        >
          <option value="" disabled hidden>
            Configuration
          </option>
          {availableConfigurationsForCircuit?.map((config: Configuration) => (
            <option key={config.globalUniqueId} value={config.globalUniqueId}>
              {config.name}
            </option>
          ))}
        </select>
      </div>
      <TrackMapPreview />
      <div className={styles.saveBar}>
        <button
          className={styles.saveButton}
          onClick={handleSave}
          disabled={!selectedCircuit || !selectedConfiguration}
        >
          Set Track
        </button>
        {hasUnsavedChanges ? (
          <div className={styles.warning}>NOT SAVED</div>
        ) : (
          <div className={styles.saved}>SAVED</div>
        )}
      </div>
    </div>
  );
}
